import { all, call, fork, put, select } from 'redux-saga/effects';
import { AxiosResponse, AxiosError } from 'axios';
import keyBy from 'lodash.keyby';
import { SagaIterator } from 'redux-saga';
import get from 'lodash.get';

// Global Utils
import axiosPoll from 'utils/axiosPoll';
import { takeLatestWithCancel } from 'utils/redux-saga';
import { formatEntities } from 'services/entities/utils';

// Global Actions
// @ts-ignore
import { mergeSeasons } from 'services/seasons/actions';
// @ts-ignore
import { setWorkTypes } from 'services/workTypes/actions';
// @ts-ignore
import { setUserEntityAssignments } from 'services/userEntityAssignments/actions';
// @ts-ignore
import { setCurrentOrganizationId } from 'services/currentOrganizationId/actions';
import { setHarvestPrintProperties } from 'services/harvestPrintProperties/actions';
import { setEntities } from 'services/entities/actions';
import { setProducts } from 'services/products/actions';
import { setEquipment } from 'services/equipment/actions';
import { setEquipmentSets } from 'services/equipmentSets/actions';
import { setTimesheetHolidays } from 'services/timesheetHolidays/actions';
import { setActiveIngredients } from 'services/activeIngredients/actions';
import { setOrganizationConfig } from 'services/organizationConfig/actions';
import { formatTimesheetHolidays } from 'services/timesheetHolidays/utils';
import { setPayrollExports } from 'services/payrollExports/actions';

// Local Constants
import { setFertilizerProducts } from 'services/fertilizerProducts/actions';
import {
    SCAFFOLD_DATA_LOAD_REQUESTED,
    SCAFFOLD_DATA_LOAD_CANCELED
} from './constants';

// Local Actions
import { scaffoldDataLoaded, failedLoadScaffoldData } from './actions';

// Local Selectors
import { selectLoadScaffoldDataParams } from './params';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadScaffoldDataSucceeded(
    response: AxiosResponse
): SagaIterator {
    const {
        seasons,
        workTypes,
        userEntityAssignments,
        harvestBinSuitabilities,
        harvestGrowingMethods,
        harvestLocations,
        harvestPalletTypes,
        harvestStorageTypes,
        harvestSubmissionProfiles,
        entities,
        products,
        equipmentSets,
        equipment,
        timesheetHolidays,
        activeIngredients,
        organizationConfigs,
        payrollExports,
        fertilizerProducts
    } = response.data;

    yield all([
        // Succeeded Load Scaffold Data
        put(scaffoldDataLoaded(response.data)),

        // Merge Seasons
        put(mergeSeasons(seasons)),

        // Set Work Types
        put(setWorkTypes(keyBy(workTypes, 'id'))),

        // Set Products
        put(setProducts(keyBy(products, 'uuid'))),

        // Set Equipment Sets
        put(setEquipmentSets(keyBy(equipmentSets, 'id'))),

        // Set Equipment
        put(setEquipment(keyBy(equipment, 'uuid'))),

        // Set Entities
        put(setEntities(keyBy(formatEntities(entities), 'uuid'))),

        // Timesheet Holidays
        put(
            setTimesheetHolidays(
                keyBy(formatTimesheetHolidays(timesheetHolidays), 'uuid')
            )
        ),

        // Set Active Ingredients
        put(setActiveIngredients(keyBy(activeIngredients, 'uuid'))),

        // Set Organization Configs
        put(setOrganizationConfig(organizationConfigs[0])),

        // Set Harvest Print Properties
        put(
            setHarvestPrintProperties({
                harvestBinSuitabilities: keyBy(harvestBinSuitabilities, 'id'),
                harvestGrowingMethods: keyBy(harvestGrowingMethods, 'id'),
                harvestLocations: keyBy(harvestLocations, 'id'),
                harvestPalletTypes: keyBy(harvestPalletTypes, 'id'),
                harvestStorageTypes: keyBy(harvestStorageTypes, 'id'),
                harvestSubmissionProfiles: keyBy(
                    harvestSubmissionProfiles,
                    'id'
                )
            })
        ),

        // Set User Entity Assignments
        // TODO use userEmail instead of email in userEntityAssignments so that we can remove this mapping
        put(
            setUserEntityAssignments(
                userEntityAssignments.map(
                    ({ userEmail, ...userEntityAssignment }: any) => ({
                        ...userEntityAssignment,
                        email: userEmail
                    })
                )
            )
        ),

        // Set Payroll Exports
        put(setPayrollExports(payrollExports)),

        // Set Fertilizer Products
        put(setFertilizerProducts(keyBy(fertilizerProducts, 'uuid')))
    ]);
}

export function* loadScaffoldDataFailed(error: AxiosError): SagaIterator {
    /**
     * If the status code is 401 we assume the user doesn't have permission to view the organisation.
     * So we remove their currentOrganizationId causing them to be redirected back to the organisations page.
     */
    if (get(error, 'response.data.statusCode') === 401) {
        yield all([
            put(failedLoadScaffoldData(error)),
            put(setCurrentOrganizationId(null))
        ]);
    } else {
        yield put(failedLoadScaffoldData(error));
    }
}

export function* loadScaffoldData(): SagaIterator {
    // Select params
    const { shape, ...params } = yield select(selectLoadScaffoldDataParams);

    yield call(axiosPoll, {
        config: {
            method: 'POST',
            url: `${REACT_APP_API_URL}/v1/Data`,
            params,
            data: {
                shape
            }
        },
        succeeded: loadScaffoldDataSucceeded,
        failed: loadScaffoldDataFailed,
        persistOffline: true
    });
}

export function* watchScaffoldDataLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        SCAFFOLD_DATA_LOAD_REQUESTED,
        SCAFFOLD_DATA_LOAD_CANCELED,
        loadScaffoldData
    );
}

export default function* scaffoldDataSagas(): SagaIterator {
    yield all([fork(watchScaffoldDataLoadRequested)]);
}
